import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";

const API = `${window.location.origin}/api`;
const SITE_ORIGIN = "https://sustainablehandymanservices.uk";

const prettyCategory = (c) => (c || "").replace(/_/g, " ").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const absoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${window.location.origin}${url}`;
};


export default function GalleryIndexPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { bizInfo, services } = usePublicData();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gallery`).then((res) => {
      if (cancelled) return;
      setItems(res.data?.items || []);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const categories = Array.from(new Set(items.map((i) => i.category).filter(Boolean)));
  const visibleItems = filter === "all" ? items : items.filter((i) => i.category === filter);

  useSeoHeadTags({
    title: "Project Gallery | Sustainable Handyman Services | Abergavenny Carpenter",
    description: "Browse completed projects by Sustainable Handyman Services — bespoke carpentry, furniture builds and repairs across Abergavenny and Monmouthshire.",
    canonical: `${SITE_ORIGIN}/gallery`,
    ogType: "website",
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">Our Work</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading mb-4" data-testid="gallery-index-title">Project Gallery</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A look at projects we've completed across Abergavenny, Monmouthshire and the surrounding Welsh valleys.
          </p>
        </div>
      </section>

      {/* Category filter — always render the 'All' control even when there's only one
          category, for consistent navigation as the gallery grows. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
        <div className="flex flex-wrap gap-2 justify-center">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
            data-testid="gallery-filter-all"
          >
            All ({items.length})
          </Button>
          {categories.map((c) => (
            <Button
              key={c}
              variant={filter === c ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(c)}
              data-testid={`gallery-filter-${c}`}
            >
              {prettyCategory(c)} ({items.filter((i) => i.category === c).length})
            </Button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <p className="text-center text-muted-foreground">Loading…</p>
        ) : visibleItems.length === 0 ? (
          <div className="border rounded-lg p-10 text-center text-muted-foreground">
            <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No projects published yet.</p>
            <p className="text-sm">Check back soon — we add new projects regularly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleItems.map((item) => {
              const cover = absoluteUrl(item.photos?.[0]);
              const href = item.slug ? `/gallery/${item.slug}` : `/gallery`;
              return (
                <Link key={item.id} to={href} className="group" data-testid={`gallery-index-card-${item.slug || item.id}`}>
                  <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow">
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      {cover ? (
                        <img
                          src={cover}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          width="800"
                          height="600"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <ImageIcon className="h-10 w-10" />
                        </div>
                      )}
                      {item.photos?.length > 1 && (
                        <Badge className="absolute bottom-2 left-2 bg-black/60 text-white border-0">
                          {item.photos.length} photos
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold font-heading group-hover:text-primary transition-colors line-clamp-2">{item.title}</h3>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-shrink-0 mt-1" />
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{prettyCategory(item.category)}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA footer */}
      <section className="bg-primary/5 py-12 px-4 sm:px-6 lg:px-8 mt-auto">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading mb-3">Got a project in mind?</h2>
          <p className="text-muted-foreground mb-5">Send us a quick message and a few photos — we'll come back with a fair quote within 48 hours.</p>
          <Link to="/enquiry">
            <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
              Request a Quote
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter bizInfo={bizInfo} services={services} />
    </div>
  );
}
